import React, { useEffect, useRef, useState, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Smartphone, CheckCircle2, RefreshCw, Copy } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

/**
 * QRConnectModal — scan-with-phone social channel onboarding.
 *
 * Flow: desktop mints a one-shot token → renders QR pointing at
 * ``/api/qr-connect/{token}`` → phone opens URL → server 302s to Meta
 * OAuth → user approves in one tap (already logged in) → callback saves
 * credentials → we poll ``.../qr-status`` and close on connection.
 */
export const QRConnectModal = ({ chatbotId, platform, label, open, onOpenChange, onConnected }) => {
  const [state, setState] = useState({ status: 'loading', token: null, url: null, expiresAt: null });
  const pollRef = useRef(null);

  const mint = useCallback(async () => {
    setState({ status: 'loading', token: null, url: null, expiresAt: null });
    try {
      const r = await api.post(`/chatbots/${chatbotId}/channels/${platform}/qr-connect`);
      setState({
        status: 'waiting',
        token: r.data?.token,
        url: r.data?.mobile_url,
        expiresAt: r.data?.expires_at,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not start QR connection';
      setState({ status: 'error', error: msg });
    }
  }, [chatbotId, platform]);

  // Mint a new token every time the modal opens.
  useEffect(() => {
    if (!open) return;
    mint();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [open, mint]);

  // Poll status while waiting.
  useEffect(() => {
    if (!open || !state.token || state.status === 'connected' || state.status === 'error' || state.status === 'expired') {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) clearInterval(pollRef.current);
    // Cap polling at the token's expires_at (defaults to +10 min). This
    // prevents a modal left open all day from burning requests forever.
    const deadline = state.expiresAt ? new Date(state.expiresAt).getTime() : Date.now() + 10 * 60 * 1000;
    pollRef.current = setInterval(async () => {
      if (Date.now() > deadline) {
        setState((prev) => ({ ...prev, status: 'expired' }));
        return;
      }
      try {
        const r = await api.get(`/chatbots/${chatbotId}/channels/${platform}/qr-status`, {
          params: { token: state.token },
        });
        const s = r.data?.status;
        if (s === 'connected') {
          setState((prev) => ({ ...prev, status: 'connected' }));
          toast.success(`${label} connected successfully!`);
          if (typeof onConnected === 'function') onConnected();
        } else if (s === 'expired' || s === 'invalid') {
          setState((prev) => ({ ...prev, status: 'expired' }));
        } else if (s === 'in_progress') {
          setState((prev) => ({ ...prev, status: 'in_progress' }));
        }
      } catch { /* ignore transient poll errors */ }
    }, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, state.token, state.status, state.expiresAt, chatbotId, platform, label, onConnected]);

  const copyLink = async () => {
    if (!state.url) return;
    try { await navigator.clipboard.writeText(state.url); toast.success('Link copied — send it to your phone'); } catch { toast.error('Copy failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid={`qr-connect-modal-${platform}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <Smartphone className="w-5 h-5 text-purple-600" />
            Connect {label} with your phone
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Scan the QR code with your phone camera. You&apos;ll approve access in one tap because you&apos;re already logged in on your phone.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-4">
          {state.status === 'loading' && (
            <div className="flex flex-col items-center gap-3 py-8">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              <p className="text-sm text-slate-500">Preparing your secure QR code…</p>
            </div>
          )}

          {state.status === 'error' && (
            <div className="text-center py-6 space-y-3">
              <p className="text-sm text-red-600" data-testid={`qr-error-${platform}`}>{state.error}</p>
              <Button variant="outline" size="sm" onClick={mint}><RefreshCw className="w-4 h-4 mr-2" /> Try again</Button>
            </div>
          )}

          {(state.status === 'waiting' || state.status === 'in_progress') && state.url && (
            <>
              <div className="p-4 bg-white border-2 border-slate-200 rounded-2xl shadow-sm">
                <QRCodeSVG value={state.url} size={220} level="M" includeMargin={false} data-testid={`qr-code-${platform}`} />
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <div className="w-2 h-2 rounded-full bg-purple-500 animate-pulse"></div>
                {state.status === 'in_progress'
                  ? 'Waiting for you to approve on your phone…'
                  : 'Waiting for scan… (link expires in 10 minutes)'}
              </div>
              <div className="w-full">
                <p className="text-xs text-slate-500 text-center mb-2">
                  Can&apos;t scan? Send this link to your phone:
                </p>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={state.url}
                    className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5 truncate text-slate-700"
                    data-testid={`qr-link-${platform}`}
                    onFocus={(e) => e.target.select()}
                  />
                  <Button size="sm" variant="outline" onClick={copyLink} data-testid={`qr-copy-${platform}`}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </>
          )}

          {state.status === 'expired' && (
            <div className="text-center py-6 space-y-3">
              <p className="text-sm text-amber-600">This QR code expired.</p>
              <Button variant="outline" size="sm" onClick={mint} data-testid={`qr-regenerate-${platform}`}>
                <RefreshCw className="w-4 h-4 mr-2" /> Generate a new QR
              </Button>
            </div>
          )}

          {state.status === 'connected' && (
            <div className="text-center py-6 space-y-3">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
              <p className="text-sm font-medium text-slate-800">{label} connected successfully.</p>
              <Button size="sm" onClick={() => onOpenChange?.(false)} data-testid={`qr-done-${platform}`}>Done</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default QRConnectModal;
